import {
    Box,
    Button,
    Container,
    Divider,
    Flex,
    Grid,
    MediaQuery,
    Stack,
    Text,
    ActionIcon,
    Tooltip,
} from "@mantine/core";
import { IconExternalLink, IconNotes } from "@tabler/icons";
import Link from "next/link";
import { nextYear } from "../../../apps/mn/seo/seoCommon";

const getSectionHeading = (title1, title2, handleOpen) => {
    {
        /** section heading */
    }
    return (
        <Grid.Col>
            <MediaQuery
                smallerThan={"sm"}
                styles={{
                    flexDirection: "column",
                    gap: 5,
                    alignItems: "start",
                }}
            >
                <Flex gap={10}>
                    <MediaQuery
                        smallerThan={"sm"}
                        styles={{
                            width: "100%",
                        }}
                    >
                        <Text
                            fz={"1.4rem"}
                            c={"violet.4"}
                            component="h2"
                            m={0}
                            w={150}
                        >
                            {title1}
                        </Text>
                    </MediaQuery>
                    {/* <Divider size="sm" orientation="vertical" /> */}
                    <Text fz={"1.4rem"} fw={"normal"} component="h2" m={0}>
                        {title2}
                    </Text>
                </Flex>
            </MediaQuery>
        </Grid.Col>
    );
};

const getSectionContent = (title, longtext, notes_link = null) => {
    longtext = longtext.replace(/\s\s+/g, " ");
    return (
        <Grid.Col>
            {/** section content */}
            <MediaQuery
                smallerThan={"sm"}
                styles={{
                    flexDirection: "column",
                    gap: 5,
                    alignItems: "start",
                }}
            >
                <Box sx={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <MediaQuery smallerThan={"sm"} styles={{ flexBasis: 0 }}>
                        <Box sx={{ flexBasis: 150, flexShrink: 0 }}>
                            <Flex align={"center"}>
                                <Text
                                    component="h3"
                                    fz={"1.05rem"}
                                    fw="bold"
                                    m={0}
                                    c={"blue"}
                                >
                                    {title}
                                </Text>
                                {!!notes_link && (
                                    <Link href={notes_link} target="_blank">
                                        <Tooltip
                                            label={`Check Notes for ${title}`}
                                        >
                                            <ActionIcon color={"blue"}>
                                                <IconExternalLink size={18} />
                                            </ActionIcon>
                                        </Tooltip>
                                    </Link>
                                )}
                            </Flex>
                        </Box>
                    </MediaQuery>
                    <Divider size="sm" orientation="vertical" />
                    <MediaQuery
                        smallerThan={"sm"}
                        styles={(theme) => ({
                            borderLeft: "4px solid red",
                            borderColor:
                                theme.colorScheme == "dark"
                                    ? theme.colors.dark[4]
                                    : theme.colors.gray[4],
                            paddingLeft: theme.spacing.md,
                        })}
                    >
                        <Text
                            component="p"
                            c={"dimmed"}
                            m={0}
                            sx={{ wordWrap: "break-word", lineHeight: 1.15 }}
                        >
                            {longtext}
                        </Text>
                    </MediaQuery>
                </Box>
            </MediaQuery>
        </Grid.Col>
    );
};

export const GateSyllabusText = ({ handleClickOpen }) => (
    <Container mb={10}>
        <Grid direction={"column"} gap={2}>
            <Grid.Col>
                <Flex align={"start"}>
                    <Text component="h1" fz={"1.5rem"} c={"green.8"}>
                        [ GATE {nextYear} ] Mining Engineering Syllabus
                    </Text>
                    <Link
                        href={"/syllabus/gate"}
                        style={{ alignSelf: "start" }}
                    >
                        <ActionIcon>
                            <IconExternalLink size={20} />
                        </ActionIcon>
                    </Link>
                </Flex>
            </Grid.Col>
            {getSectionHeading(
                "Section 1",
                "Engineering Mathematics:",
                handleClickOpen
            )}

            {getSectionContent(
                "Linear Algebra",
                `Matrices and Determinants; Inverse and Rank of matrix; Systems of linear equations; Eigen values and Eigen vectors. Cayley-Hamilton Theorem`,
                "/discuss/vzmRt4xvVgGpta2ZZt9F"
            )}

            {getSectionContent(
                "Calculus",
                `Limit, continuity and differentiability; Partial Derivatives; Mean value theorems; Indeterminate forms and L' Hospital's rule; Maxima and minima; Taylor's theorem; Sequences and series; Test for convergence; Fourier series.`,
                "/discuss/tzOiSViXowVWkRx3EnwY"
            )}

            {getSectionContent(
                "Vector Calculus",
                `Gradient; Divergence and Curl; Line; surface and volume integrals; Stokes, Gauss and Green's theorems.`,
                "/discuss/jh3eak2BjvQtmMHITh95"
            )}

            {getSectionContent(
                "Differential Equations",
                `Linear and non-linear first order ODEs; Higher order linear ODEs with constant coefficients; Cauchy's and Euler's equations.`
            )}

            {getSectionContent(
                "Probability and Statistics",
                `Measures of central tendency and dispersion; hypothesis testing; Binomial, Poisson, exponential and normal distributions; Correlation and regression analysis`
            )}

            {getSectionContent(
                "Numerical Methods",
                `Solutions of linear algebraic equations;Interpolation; Integration of trapezoidal andSimpson's rule; Single and multi-step methodsfor differential equations.`
            )}

            {getSectionHeading(
                "Section 2",
                "Mining Geology, Mine Development and Surveying:",
                handleClickOpen
            )}

            {getSectionContent(
                "Mining Geology",
                ` Minerals, Rocks and their Origin, Classification, Ore Genesis; Structural Geology.`
            )}
            {getSectionContent(
                "Mine Development",
                `Methods of access to deposits; Underground drivages; Drilling method and 
machines; Explosives and energetics, blasting devices, blast design practices; Rock-Tool Interaction 
applicable to mechanical cutting systems and their selection.`
            )}
            {getSectionContent(
                "Mine Surveying",
                `Levels and levelling, theodolite, tacheometry, triangulation; Contouring; Errors and 
adjustments; Correlation; Underground surveying; Curves; Photogrammetry; EDM, Total Station, 
GPS, Basics of GIS and remote sensing.`
            )}

            {getSectionHeading(
                "Section 3",
                "Geomechanics and Ground Control:",
                handleClickOpen
            )}

            {getSectionContent(
                "Engineering Mechanics",
                `Equivalent force systems; Equations of equilibrium; Two dimensional 
frames and trusses; Free body diagrams; Friction forces; Particle kinematics and dynamics; Beam 
analysis.`
            )}

            {getSectionContent(
                "Geomechanics",
                `Geo-technical properties of rocks; Rock mass classification; Instrumentation and insitu stress measurement techniques; Theories of rock failure; Ground vibrations; Stress distribution 
around mine openings; Subsidence; Slope stability`
            )}

            {getSectionContent(
                "Ground Control",
                `Design of pillars; Roof supporting systems; Mine filling. Strata Control and 
Monitoring Plan.`
            )}

            {getSectionHeading(
                "Section 4",
                "Mining Methods and Machinery:",
                handleClickOpen
            )}

            {getSectionContent(
                "Mining Methods",
                `Surface mining: layout, development, loading, transportation and mechanization, 
continuous surface mining systems; highwall mining; Underground coal mining: bord and pillar 
systems, room and pillar mining, longwall mining, thick seam mining methods, Underground metal 
mining: open, supported and caved stoping methods, stope mechanization, ore handling systems.`
            )}

            {getSectionContent(
                "Mining Machinery",
                `Generation and transmission of mechanical, hydraulic and pneumatic power; 
Materials handling: wire ropes, haulages, conveyors, face and development machinery, hoisting 
systems, pumps; comminution methods and machinery.`
            )}

            {getSectionHeading(
                "Section 5",
                "Surface Environment, Mine Ventilation and Underground Hazards:",
                handleClickOpen
            )}
            {getSectionContent(
                "Surface Environment",
                `Air, water and soil pollution: Standards of quality, causes and dispersion of 
contamination and control; Noise pollution and control; Land reclamation; EIA.`
            )}
            {getSectionContent(
                "Mine Ventilation",
                `Underground atmosphere; Heat load sources and thermal environment,.air cooling; 
Mechanics of airflow, distribution, natural and mechanical ventilation; Mine fans and usage; Auxiliary 
ventilation; Ventilation survey and planning; Ventilation networks. `
            )}
            {getSectionContent(
                "Underground Hazards",
                `Mine Gases, Methane drainage; Underground hazards from fires,
explosions, dust and inundation; Rescue apparatus and practices; Safety management plan; 
Accident data analysis; assessment; Mine lighting; Mine legislation; Occupational health and safety. `
            )}

            {getSectionHeading(
                "Section 6",
                "Mineral Economics, Mine Planning, Systems Engineering:",
                handleClickOpen
            )}

            {getSectionContent(
                "Mineral Economics",
                `Mineral resource classification; Discounted cash flow analysis; Mine valuation; 
Mineral taxation.`
            )}
            {getSectionContent(
                "Mine Planning",
                `Sampling methods, practices and interpretation; Reserve estimation techniques: 
Basics of geostatistics and quality control; Optimization of facility location; Mine planning and its 
components, Determination of mine size and mine life; Ultimate pit configuration and its 
determination, Optimum mill cut-off grade and its determination, Stope planning, Design of haul road, 
Selection of mining system vis-à-vis equipment system.`
            )}
            {getSectionContent(
                "Systems Engineering",
                `Concepts of reliability; Reliability of simple systems; Maintainability and 
availability; Linear programming, transportation and assignment problems; Network analysis; 
Inventory models; Queuing theory; Decision trees.`
            )}
        </Grid>
    </Container>
);
